import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box } from '@rover/kibble/core';

import Avatar, { AvatarType, getSize, Size } from '../../Avatar/Avatar';

export type FlyingAvatarRect = Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>;

type Props = {
  imageUrl: string;
  /** Viewport rect the avatar launches from (the add-to-shortlist button). */
  sourceRect: FlyingAvatarRect;
  /** Viewport rect the avatar flies to (the preview's avatar stack). */
  targetRect: FlyingAvatarRect;
  /** Called once the flight finishes so the caller can unmount this. */
  onComplete: () => void;
};

const FLY_DURATION_MS = 400;
const AVATAR_SIZE = Size.ExtraSmall;
const { diameter: AVATAR_DIAMETER } = getSize(AVATAR_SIZE);

const centerOf = (rect: FlyingAvatarRect): { x: number; y: number } => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
});

/**
 * A one-shot avatar that flies from the add-to-shortlist button to the contact
 * list preview and fades out, giving an "added to the list" cue. Rendered into
 * a portal on `document.body` so it animates in viewport space, above the page
 * and unaffected by any transformed or clipping ancestor of the source card.
 */
const FlyingAvatar = ({
  imageUrl,
  sourceRect,
  targetRect,
  onComplete,
}: Props): JSX.Element | null => {
  const [isFlying, setIsFlying] = useState(false);

  const source = centerOf(sourceRect);
  const target = centerOf(targetRect);
  const startLeft = source.x - AVATAR_DIAMETER / 2;
  const startTop = source.y - AVATAR_DIAMETER / 2;
  const dx = target.x - source.x;
  const dy = target.y - source.y;

  useEffect(() => {
    // Paint at the source first, then flip to the target on the next frame so
    // the CSS transition has two states to interpolate between.
    const frame = requestAnimationFrame(() => setIsFlying(true));
    // Timer (not `transitionend`) drives cleanup: it fires exactly once and
    // survives the case where the transition is a no-op (e.g. zero distance).
    const timer = setTimeout(onComplete, FLY_DURATION_MS + 50);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [onComplete]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <Box
      aria-hidden
      position="fixed"
      top={`${startTop}px`}
      left={`${startLeft}px`}
      width={`${AVATAR_DIAMETER}px`}
      height={`${AVATAR_DIAMETER}px`}
      zIndex={`${ZIndex.POPOVER.toNumber()}`}
      sx={{
        pointerEvents: 'none',
        transform: isFlying ? `translate(${dx}px, ${dy}px) scale(0.6)` : 'none',
        opacity: isFlying ? 0 : 1,
        transition: `transform ${FLY_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${FLY_DURATION_MS}ms ease-in`,
      }}
    >
      <Avatar
        imageUrl={imageUrl}
        avatarType={AvatarType.Person}
        size={AVATAR_SIZE}
        borderRadius={999}
        borderWidth={2}
        borderColor="white"
        objectFit="cover"
      />
    </Box>,
    document.body
  );
};

export default FlyingAvatar;
